// Shared route formatting utilities
// Used across RouteListItem, SaveRouteModal, and other components for consistent formatting

/**
 * Format distance from meters to kilometers with 1 decimal place
 * @param distanceInMeters - Distance value in meters
 * @returns Formatted string like "12.5 km" or "N/A"
 */
export const formatDistance = (distanceInMeters: number | string | undefined): string => {
  const numDistance = typeof distanceInMeters === 'string' ? parseFloat(distanceInMeters) : distanceInMeters;
  if (typeof numDistance !== 'number' || isNaN(numDistance)) return 'N/A';
  const km = numDistance / 1000;
  return `${km.toFixed(1)} km`;
};

/**
 * Format duration from seconds to hours and minutes
 * @param durationInSeconds - Duration value in seconds
 * @returns Formatted string like "2h 30m" or "45m" or "N/A"
 */
export const formatDuration = (durationInSeconds: number | string | undefined): string => {
  const numDuration = typeof durationInSeconds === 'string' ? parseFloat(durationInSeconds) : durationInSeconds;
  if (typeof numDuration !== 'number' || isNaN(numDuration)) return 'N/A';
  const hours = Math.floor(numDuration / 3600);
  const minutes = Math.floor((numDuration % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes} min`;
  }
  return `${minutes} min`;
};

/**
 * Format elevation from meters to rounded meters
 * @param elevationInMeters - Elevation value in meters
 * @returns Formatted string like "450m" or "N/A"
 */
export const formatElevation = (elevationInMeters: number | string | undefined): string => {
  const numElevation = typeof elevationInMeters === 'string' ? parseFloat(elevationInMeters) : elevationInMeters;
  if (typeof numElevation !== 'number' || isNaN(numElevation)) return 'N/A';
  return `${Math.round(numElevation)}m`;
};

/**
 * Get display name for bike profile
 * @param profile - Bike profile key
 * @returns Human-readable profile name
 */
export const getBikeProfileDisplayName = (profile: string): string => {
  const profiles: Record<string, string> = {
    'road': 'Road',
    'mtb': 'Mountain',
    'gravel': 'Gravel',
    'trekking': 'Trekking',
    'fastbike': 'Fast Bike',
    'bike': 'General'
  };
  return profiles[profile] || 'Unknown';
};
